import { useCallback, useMemo, useState } from "react";

import {
  RiskService,
  type RiskScoreListResponse,
  type RiskScoreSnapshotResponse,
} from "@/api";
import { useApi } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatDateID } from "@/lib/format";
import { formatScore } from "@/lib/risk-helpers";
import { DIMENSION_KEY } from "@/components/risk/risk-dimension-keys";
import { Alert } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DeltaIndicator } from "@/components/shared/delta-indicator";
import { Pagination } from "@/components/shared/pagination";
import { ScoreBandBadge } from "@/components/shared/score-band-badge";
import { RiskSnapshotDetailDialog } from "./risk-snapshot-detail-dialog";

const PAGE_SIZE = 25;

export function RiskSnapshotsTab() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState<RiskScoreSnapshotResponse | null>(null);

  const offset = page * PAGE_SIZE;
  // ``deps`` ties the fetcher to page so useApi re-runs on
  // navigation. The hook itself does not cache between renders, but
  // refetching is cheap (backend returns the trailing 90 days).
  const fetcher = useCallback(
    () => RiskService.listSnapshots({ limit: PAGE_SIZE, offset }),
    [offset]
  );
  const { data, error, isLoading } = useApi<RiskScoreListResponse>(fetcher, {
    deps: [offset],
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const hasNext = offset + items.length < total;

  return (
    <div className="space-y-4">
      {error && <Alert variant="warning">{parseApiError(error)}</Alert>}

      <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("risk.snapshots.colTime")}</TableHead>
              <TableHead>{t("risk.snapshots.colOverall")}</TableHead>
              <TableHead className="hidden tablet:table-cell">
                {t(DIMENSION_KEY.security)}
              </TableHead>
              <TableHead className="hidden tablet:table-cell">
                {t(DIMENSION_KEY.policy)}
              </TableHead>
              <TableHead className="hidden tablet:table-cell">
                {t(DIMENSION_KEY.asset)}
              </TableHead>
              <TableHead className="hidden tablet:table-cell">
                {t(DIMENSION_KEY.compliance)}
              </TableHead>
              <TableHead>{t("risk.snapshots.colDelta")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 && (
              <>
                {[0, 1, 2, 3, 4].map((i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell colSpan={7} className="py-4">
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!isLoading && items.length === 0 && (
              <TableEmpty
                colSpan={7}
                message={t("risk.snapshots.empty")}
              />
            )}
            {items.map((snap) => (
              <TableRow
                key={snap.id}
                onClick={() => setDetail(snap)}
                className="cursor-pointer"
              >
                <TableCell className="whitespace-nowrap text-neutral-700">
                  {formatDateID(snap.computed_at)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-ocean-900">
                      {formatScore(snap.overall_score)}
                    </span>
                    <ScoreBandBadge band={snap.score_band} />
                  </div>
                </TableCell>
                <TableCell className="hidden tablet:table-cell">
                  {formatScore(snap.security_score)}
                </TableCell>
                <TableCell className="hidden tablet:table-cell">
                  {formatScore(snap.policy_score)}
                </TableCell>
                <TableCell className="hidden tablet:table-cell">
                  {formatScore(snap.asset_score)}
                </TableCell>
                <TableCell className="hidden tablet:table-cell">
                  {formatScore(snap.compliance_score)}
                </TableCell>
                <TableCell>
                  <DeltaIndicator delta={snap.delta_overall} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {items.length > 0 && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          hasNext={hasNext}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => p + 1)}
        />
      )}

      <RiskSnapshotDetailDialog
        snapshot={detail}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}
